import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { ImageUploader } from "../components/VideoUploader";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { ShieldAlert, ArrowRight, Sparkles, Loader2, Video, X, RefreshCw, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import VideoRecorderModal from "../components/VideoRecorderModal";

const PHASE_MAP = {
  queued: { label: "Queued…", pct: 5 },
  validating: { label: "Validating video…", pct: 12 },
  uploading: { label: "Uploading to storage…", pct: 25 },
  thumbnail: { label: "Generating thumbnail (FFmpeg)…", pct: 45 },
  transcribing: { label: "Transcribing audio (Whisper)…", pct: 70 },
  describing: { label: "Writing AI description (Claude)…", pct: 90 },
  complete: { label: "Done", pct: 100 },
};

/* AI video uploader using Server-Sent Events for real-time progress */
function AIVideoUploader({ title, onProcessed, disabled }) {
  const inputRef = React.useRef(null);
  const [processing, setProcessing] = useState(false);
  const [phase, setPhase] = useState("queued");
  const [preview, setPreview] = useState(null);
  const [duration, setDuration] = useState(0);
  const [result, setResult] = useState(null);
  const [recorderOpen, setRecorderOpen] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!title || title.trim().length < 3) {
      toast.error("Please enter a title (at least 3 characters) first.");
      // Focus the title input so user knows where to go
      document.querySelector('[data-testid="create-title-input"]')?.focus();
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    if (!file.type.startsWith("video/")) { toast.error("Please upload a valid video file (MP4, MOV, or WEBM)."); return; }

    // Client-side duration guard (server also enforces via ffprobe). Allow a small buffer for
    // phone recordings that come in as 30.2s from a 30-second capture.
    const videoEl = document.createElement("video");
    videoEl.preload = "metadata";
    videoEl.onloadedmetadata = async () => {
      window.URL.revokeObjectURL(videoEl.src);
      const dur = videoEl.duration;
      if (Number.isFinite(dur) && dur > 33) {
        toast.error(`Video is ${Math.round(dur)}s long. Maximum is 30 seconds — please trim your clip.`);
        return;
      }
      setDuration(Number.isFinite(dur) ? dur : 30);
      setPreview(URL.createObjectURL(file));
      setProcessing(true);
      setPhase("queued");

      try {
        // Step 1: start the job
        const formData = new FormData();
        formData.append("file", file);
        formData.append("title", title);
        const startRes = await api.post("/upload/video/start", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000,
        });
        const jobId = startRes.data.job_id;

        // Step 2: consume SSE progress stream
        const token = localStorage.getItem("tb_token");
        const es = new EventSource(`${API_BASE}/upload/video/progress/${jobId}?token=${token}`);

        es.addEventListener("phase", (e) => {
          setPhase(e.data);
        });
        es.addEventListener("complete", (e) => {
          const data = JSON.parse(e.data);
          setResult(data);
          onProcessed?.(data);
          toast.success("Video processed. AI description ready.");
          setProcessing(false);
          es.close();
        });
        es.addEventListener("error", (e) => {
          let msg = "Processing failed";
          try {
            if (e.data) msg = JSON.parse(e.data).message || e.data;
          } catch (_) {}
          toast.error(msg);
          setProcessing(false);
          es.close();
        });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Upload failed");
        setProcessing(false);
      }
    };
    videoEl.src = URL.createObjectURL(file);
  };

  const clear = () => {
    setPreview(null); setResult(null); onProcessed?.(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const pct = PHASE_MAP[phase]?.pct ?? 0;
  const label = PHASE_MAP[phase]?.label ?? phase;

  return (
    <div data-testid="ai-video-uploader" className="border border-dashed border-border rounded-2xl bg-secondary/40 p-6">
      {!preview ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            type="button"
            className={`text-center rounded-xl border border-dashed border-border py-8 transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-secondary/60"}`}
            onClick={() => !disabled && inputRef.current?.click()}
            disabled={disabled}
            data-testid="ai-video-drop"
          >
            <div className="w-12 h-12 rounded-full bg-[hsl(var(--primary-soft))] mx-auto mb-3 flex items-center justify-center">
              <Upload className="w-6 h-6 text-[hsl(var(--primary))]" />
            </div>
            <p className="font-serif text-lg mb-1">Upload from device</p>
            <p className="text-xs text-muted-foreground">MP4 · MOV · WEBM · Max 30s</p>
          </button>
          <button
            type="button"
            className={`text-center rounded-xl border border-dashed border-border py-8 transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-secondary/60"}`}
            onClick={() => !disabled && setRecorderOpen(true)}
            disabled={disabled}
            data-testid="ai-video-record"
          >
            <div className="w-12 h-12 rounded-full bg-[hsl(var(--oppose))]/10 mx-auto mb-3 flex items-center justify-center">
              <Video className="w-6 h-6 text-[hsl(var(--oppose))]" />
            </div>
            <p className="font-serif text-lg mb-1">Record with camera</p>
            <p className="text-xs text-muted-foreground">Straight from your webcam · Max 30s</p>
          </button>
          <p className="col-span-full text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1">
            <Sparkles className="w-3 h-3" /> AI auto-generates thumbnail &amp; description
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="relative bg-black rounded-xl overflow-hidden">
            <video
              src={preview}
              controls
              onLoadedMetadata={(e) => {
                const v = e.currentTarget;
                if (v.duration === Infinity || Number.isNaN(v.duration)) {
                  const onSeeked = () => { v.currentTime = 0; v.removeEventListener("seeked", onSeeked); };
                  v.addEventListener("seeked", onSeeked);
                  try { v.currentTime = 1e10; } catch (_) { /* noop */ }
                }
              }}
              className="w-full max-h-[400px] mx-auto"
            />
            {processing && (
              <div className="absolute inset-0 bg-background/90 flex items-center justify-center">
                <div className="text-center w-full px-8">
                  <Loader2 className="w-10 h-10 animate-spin text-[hsl(var(--primary))] mx-auto mb-4" />
                  <p className="font-serif text-lg mb-1" data-testid="ai-video-phase-label">{label}</p>
                  <p className="text-xs text-muted-foreground mb-4">{pct}%</p>
                  <div className="max-w-md mx-auto h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-[hsl(var(--primary))] transition-all duration-500 ease-out"
                      style={{ width: `${pct}%` }}
                      data-testid="ai-video-progress-bar"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              {duration.toFixed(1)}s · {result ? "Processed" : processing ? label : "Pending"}
            </div>
            {!processing && (
              <Button variant="outline" size="sm" className="rounded-full" onClick={clear} data-testid="ai-video-clear">
                <X className="w-4 h-4 mr-1" /> Remove
              </Button>
            )}
          </div>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="ai-video-input"
      />
      <VideoRecorderModal
        open={recorderOpen}
        onClose={() => setRecorderOpen(false)}
        onCaptured={(file) => { setRecorderOpen(false); handleFile(file); }}
      />
    </div>
  );
}


export default function Create() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [kind, setKind] = useState("debate");
  const [title, setTitle] = useState("");
  const [processed, setProcessed] = useState(null); // { video_url, thumbnail_url, description, transcript }
  const [customThumb, setCustomThumb] = useState(null);
  const [position, setPosition] = useState("support");
  const [visibility, setVisibility] = useState("public");
  const [allowReplies, setAllowReplies] = useState(true);
  const [posting, setPosting] = useState(false);
  // Private audience selection
  const [myGroups, setMyGroups] = useState([]);
  const [myFollowers, setMyFollowers] = useState([]);
  const [allowedGroups, setAllowedGroups] = useState([]); // array of group ids
  const [allowedUsers, setAllowedUsers] = useState([]);   // array of user ids

  React.useEffect(() => {
    if (visibility === "private" && (myGroups.length === 0 && myFollowers.length === 0)) {
      Promise.all([
        api.get("/users/me/groups").then((r) => setMyGroups(r.data)).catch(() => {}),
        api.get("/users/me/followers").then((r) => setMyFollowers(r.data)).catch(() => {}),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibility]);

  if (user?.verification_status !== "verified") {
    return (
      <div className="max-w-2xl mx-auto p-6 lg:p-16 text-center">
        <div className="border border-[hsl(var(--primary))] bg-[hsl(var(--primary-soft))] rounded-2xl p-10">
          <ShieldAlert className="w-10 h-10 mx-auto text-[hsl(var(--primary))] mb-4" />
          <div className="text-xs font-semibold text-[hsl(var(--primary))] mb-3">VERIFICATION REQUIRED</div>
          <h2 className="font-serif text-3xl mb-3">Only verified users can post</h2>
          <p className="text-muted-foreground mb-6">
            Complete Aadhaar verification to unlock posting.
          </p>
          <Link to="/verify">
            <Button className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="create-verify-cta">
              Verify identity <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const finalThumbnail = customThumb || processed?.thumbnail_url;
  const canSubmit = title && processed?.video_url && processed?.description;

  const submit = async () => {
    if (!canSubmit) { toast.error("Complete video processing first"); return; }
    setPosting(true);
    try {
      if (kind === "debate") {
        const res = await api.post("/debates", {
          title,
          description: processed.description,
          video_url: processed.video_url,
          thumbnail_url: finalThumbnail,
          position,
          visibility,
          allow_replies: allowReplies,
          allowed_groups: visibility === "private" ? allowedGroups : [],
          allowed_users: visibility === "private" ? allowedUsers : [],
        });
        toast.success("Debate posted");
        navigate(`/debates/${res.data.id}`);
      } else {
        await api.post("/thoughts", {
          title,
          description: processed.description,
          video_url: processed.video_url,
          thumbnail_url: finalThumbnail,
          visibility,
        });
        toast.success("Thought posted");
        navigate("/thoughts");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post");
    } finally { setPosting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6 lg:py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="text-xs font-semibold text-muted-foreground mb-2 tracking-wider">NEW POST</div>
        <h1 className="font-serif text-4xl">Broadcast your position</h1>
      </div>

      {/* Kind toggle */}
      <div className="grid grid-cols-2 gap-3 mb-8">
        <button
          onClick={() => setKind("debate")}
          data-testid="create-kind-debate"
          className={`p-5 text-left rounded-2xl border transition-all ${
            kind === "debate"
              ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-soft))]"
              : "border-border hover:bg-muted"
          }`}
        >
          <div className="text-xs font-semibold text-muted-foreground mb-2 tracking-wider">DEBATE</div>
          <div className="font-serif text-lg">Structured argument with support/oppose replies</div>
        </button>
        <button
          onClick={() => setKind("thought")}
          data-testid="create-kind-thought"
          className={`p-5 text-left rounded-2xl border transition-all ${
            kind === "thought"
              ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-soft))]"
              : "border-border hover:bg-muted"
          }`}
        >
          <div className="text-xs font-semibold text-muted-foreground mb-2 tracking-wider">THOUGHT</div>
          <div className="font-serif text-lg">Short opinion video, no debate tree</div>
        </button>
      </div>

      <div className="space-y-6">
        {/* Title first — needed for AI description */}
        <div>
          <Label className="text-sm font-semibold">Title</Label>
          <Input
            className="rounded-xl mt-2 h-12 bg-card border-border font-serif text-lg"
            placeholder={kind === "debate" ? "e.g. Should India ban single-use plastic by 2027?" : "e.g. AI won't replace teachers — here's why"}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="create-title-input"
          />
          <p className="text-xs text-muted-foreground mt-1.5">Set your title before uploading — it guides the AI description. (Min 3 characters.)</p>
        </div>

        {/* Video uploader with AI processing */}
        <div>
          <Label className="text-sm font-semibold mb-2 block">Video (max 30s)</Label>
          <AIVideoUploader title={title} onProcessed={setProcessed} disabled={!title || title.trim().length < 3} />
        </div>

        {/* AI-generated description (read-only) */}
        {processed && (
          <div className="border border-[hsl(var(--primary))] bg-[hsl(var(--primary-soft))] rounded-2xl p-5" data-testid="ai-description-card">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" />
              <div className="text-xs font-semibold text-[hsl(var(--primary))] tracking-wider">AI-GENERATED DESCRIPTION</div>
            </div>
            <p className="font-serif text-lg leading-snug" data-testid="ai-description-text">
              {processed.description}
            </p>
            {processed.transcript && (
              <details className="mt-4">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                  View transcript ({processed.transcript.length} chars)
                </summary>
                <p className="text-xs text-muted-foreground mt-2 font-mono leading-relaxed bg-card p-3 rounded-lg border border-border">
                  {processed.transcript}
                </p>
              </details>
            )}
            <p className="text-xs text-muted-foreground mt-3">
              This description was generated from your video's audio + title. It cannot be edited.
            </p>
          </div>
        )}

        {/* Thumbnail override */}
        {processed && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Thumbnail</Label>
              <span className="text-xs text-muted-foreground">Auto-generated from your video. Change if you like.</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-border rounded-2xl overflow-hidden bg-card">
                <div className="aspect-video bg-black flex items-center justify-center">
                  {finalThumbnail ? (
                    <img src={fileUrl(finalThumbnail)} alt="thumb" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs text-muted-foreground">No thumbnail</span>
                  )}
                </div>
                <div className="px-3 py-2 text-xs text-muted-foreground">
                  {customThumb ? "Custom" : "AI-generated"}
                </div>
              </div>
              <div>
                <ImageUploader onUploaded={setCustomThumb} testId="thumb-uploader" label="Upload custom" />
                {customThumb && (
                  <button
                    onClick={() => setCustomThumb(null)}
                    className="text-xs text-muted-foreground hover:text-foreground mt-2 flex items-center gap-1"
                    data-testid="thumb-revert"
                  >
                    <RefreshCw className="w-3 h-3" /> Revert to AI thumbnail
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {kind === "debate" && (
          <>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Your position on this topic</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPosition("support")}
                  data-testid="create-position-support"
                  className={`p-4 rounded-xl border transition-colors ${
                    position === "support"
                      ? "border-[hsl(var(--support))] bg-[hsl(var(--support-soft))] text-[hsl(var(--support))]"
                      : "border-border hover:bg-muted"
                  }`}
                >
                  <div className="text-xs font-semibold tracking-wider mb-1">SUPPORT</div>
                  <div className="text-sm">I am for this topic</div>
                </button>
                <button
                  onClick={() => setPosition("oppose")}
                  data-testid="create-position-oppose"
                  className={`p-4 rounded-xl border transition-colors ${
                    position === "oppose"
                      ? "border-[hsl(var(--oppose))] bg-[hsl(var(--oppose-soft))] text-[hsl(var(--oppose))]"
                      : "border-border hover:bg-muted"
                  }`}
                >
                  <div className="text-xs font-semibold tracking-wider mb-1">OPPOSE</div>
                  <div className="text-sm">I am against this topic</div>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between border border-border rounded-2xl p-4">
              <div>
                <div className="text-sm font-semibold">Allow video replies</div>
                <div className="text-xs text-muted-foreground mt-0.5">Others can add support/oppose replies</div>
              </div>
              <Switch checked={allowReplies} onCheckedChange={setAllowReplies} data-testid="create-allow-replies" />
            </div>
          </>
        )}

        <div>
          <Label className="text-sm font-semibold mb-2 block">Visibility</Label>
          <div className="grid grid-cols-2 gap-3">
            {["public", "private"].map((v) => (
              <button
                key={v}
                onClick={() => setVisibility(v)}
                data-testid={`create-visibility-${v}`}
                className={`p-3 rounded-xl border transition-colors text-sm font-semibold ${
                  visibility === v ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]" : "border-border hover:bg-muted"
                }`}
              >
                {v[0].toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          {visibility === "private" && kind === "debate" && (
            <div className="mt-4 space-y-4 border border-[hsl(var(--primary))] bg-[hsl(var(--primary-soft))] rounded-2xl p-5" data-testid="private-audience">
              <div className="text-xs font-semibold text-[hsl(var(--primary))] tracking-wider">
                CHOOSE YOUR AUDIENCE
              </div>
              <p className="text-xs text-muted-foreground -mt-2">
                Pick specific groups and/or followers who can see this debate. At least one is required for a private post.
              </p>

              {/* Groups */}
              <div>
                <div className="text-sm font-semibold mb-2">Your groups</div>
                {myGroups.length === 0 ? (
                  <div className="text-xs text-muted-foreground bg-card rounded-lg border border-border p-3">
                    You're not in any group yet. <Link to="/groups" className="text-[hsl(var(--primary))] underline">Create or join a group</Link>.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {myGroups.map((g) => {
                      const on = allowedGroups.includes(g.id);
                      return (
                        <button
                          key={g.id}
                          onClick={() =>
                            setAllowedGroups((prev) => on ? prev.filter((x) => x !== g.id) : [...prev, g.id])
                          }
                          data-testid={`private-group-${g.id}`}
                          className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${
                            on ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white" : "border-border bg-card hover:bg-muted"
                          }`}
                        >
                          {g.name} · {g.members_count || 0}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Followers */}
              <div>
                <div className="text-sm font-semibold mb-2">Your followers</div>
                {myFollowers.length === 0 ? (
                  <div className="text-xs text-muted-foreground bg-card rounded-lg border border-border p-3">
                    No followers yet. Anyone in your selected groups will still see this post.
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto scrollbar-thin bg-card rounded-lg border border-border divide-y divide-border">
                    {myFollowers.map((f) => {
                      const on = allowedUsers.includes(f.id);
                      return (
                        <button
                          key={f.id}
                          onClick={() =>
                            setAllowedUsers((prev) => on ? prev.filter((x) => x !== f.id) : [...prev, f.id])
                          }
                          data-testid={`private-follower-${f.username}`}
                          className={`flex items-center gap-3 w-full p-2.5 hover:bg-muted transition-colors text-left ${on ? "bg-[hsl(var(--primary-soft))]" : ""}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary-soft))] flex items-center justify-center text-xs font-bold text-[hsl(var(--primary))]">
                            {f.username?.[0]?.toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">@{f.username}</div>
                            <div className="text-xs text-muted-foreground truncate">{f.full_name}</div>
                          </div>
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${on ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]" : "border-border"}`}>
                            {on && <span className="text-white text-[10px] leading-none">✓</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="text-xs text-muted-foreground">
                Selected: <b>{allowedGroups.length}</b> groups · <b>{allowedUsers.length}</b> followers
              </div>
            </div>
          )}
        </div>

        <Button
          onClick={submit}
          disabled={posting || !canSubmit}
          className="w-full rounded-full h-12 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
          data-testid="create-submit"
        >
          {posting ? "Publishing…" : `Publish ${kind}`}
        </Button>
      </div>
    </div>
  );
}
